import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Settings, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useState, useMemo, useEffect } from 'react';
import { assetNetBookValueData } from '@/lib/mock-data';
import { formatCurrency as formatCurrencyUtil, calculateYAxisTicks, normalizeToSVG } from '@/lib/chart-utils';
import type { Asset } from '@/types/asset';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface NBVDataPoint {
  month: string;
  value: number;
}

interface NetBookValueCardProps {
  currentValue: number; // Mock data for fallback
  monthlyData: NBVDataPoint[]; // Mock data for graph visualization (always used)
  assets?: Asset[]; // Real-time API data
  className?: string;
}

export function NetBookValueCard({
  currentValue: defaultCurrentValue,
  monthlyData: defaultMonthlyData,
  assets,
  className,
}: NetBookValueCardProps): React.ReactElement {
  const [hoveredPoint, setHoveredPoint] = useState<NBVDataPoint | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  // Get sorted assets by name from API data
  const sortedAssets = useMemo(() => {
    if (!assets || assets.length === 0) {
      return [];
    }
    return [...assets]
      .filter((asset: any) => asset.status === 'active') // Only show active assets
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [assets]);

  /**
   * Calculate current Net Book Value from API data
   * - For 'all': Sum of all assets' netBookValue
   * - For specific asset: Individual asset's netBookValue
   * - Handles null/undefined values gracefully
   */
  const currentValue = useMemo(() => {
    try {
      setError(null);

      // If no assets data available, use fallback
      if (!assets || assets.length === 0) {
        console.warn('⚠️ No assets data available, using fallback value');
        return defaultCurrentValue;
      }

      if (selectedAssetId === 'all') {
        // Calculate sum of all assets' net book values
        const totalNBV = assets.reduce((sum: any, asset: any) => {
          // Handle null/undefined/invalid values
          const nbv = typeof asset.netBookValue === 'number' && !isNaN(asset.netBookValue)
            ? asset.netBookValue
            : 0;
          return sum + nbv;
        }, 0);

        console.log('📊 Total NBV calculated:', totalNBV, 'from', assets.length, 'assets');
        return totalNBV;
      } else {
        // Get specific asset's net book value
        const selectedAsset = assets.find(a => a.id === selectedAssetId);
        if (selectedAsset) {
          const nbv = typeof selectedAsset.netBookValue === 'number' && !isNaN(selectedAsset.netBookValue)
            ? selectedAsset.netBookValue
            : 0;
          console.log('📊 Asset NBV:', selectedAsset.name, '=', nbv);
          return nbv;
        }

        // Asset not found, use fallback
        console.warn('⚠️ Selected asset not found, using fallback value');
        return defaultCurrentValue;
      }
    } catch (err) {
      console.error('❌ Error calculating NBV:', err);
      setError('Unable to calculate current value');
      return defaultCurrentValue;
    }
  }, [assets, selectedAssetId, defaultCurrentValue]);

  /**
   * Generate unique mock data for each asset with decaying trend
   * Each asset gets a different decay pattern based on its ID
   * For "All", sum all individual asset values at each time point
   */
  const monthlyData = useMemo(() => {
    const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'];

    // Helper function to generate decay pattern for a specific asset
    const generateAssetDecayData = (assetId: string, acquisitionCost: number): NBVDataPoint[] => {
      // Use asset ID to create a unique seed for consistent but different patterns
      const seed = assetId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

      // Different decay rates based on seed (between 2% and 5% per month)
      const baseDecayRate = 0.02 + (seed % 30) / 1000; // 2.0% to 4.9%

      // Add some variation to make it more realistic
      const variation = (seed % 10) / 100; // 0% to 9% variation

      return months.map((month, index) => {
        // Calculate decay with slight randomness based on seed
        const monthVariation = ((seed * (index + 1)) % 10) / 100;
        const decayFactor = Math.pow(1 - baseDecayRate - monthVariation * variation, index);
        const value = Math.round(acquisitionCost * decayFactor);

        return {
          month,
          value: Math.max(0, value), // Ensure non-negative
        };
      });
    };

    try {
      if (!assets || assets.length === 0) {
        console.warn('⚠️ No assets data available for graph, using default mock data');
        return defaultMonthlyData;
      }

      if (selectedAssetId === 'all') {
        // Calculate sum of all individual asset values at each time point
        const allAssetData = assets.map((asset: any) => {
          const cost = typeof asset.acquisitionCost === 'number' && !isNaN(asset.acquisitionCost)
            ? asset.acquisitionCost
            : 0;
          return generateAssetDecayData(asset.id, cost);
        });

        // Sum values at each time point
        return months.map((month, index) => ({
          month,
          value: allAssetData.reduce((sum, assetData) => sum + assetData[index].value, 0),
        }));
      } else {
        // Generate unique decay pattern for selected asset
        const selectedAsset = assets.find(a => a.id === selectedAssetId);
        if (selectedAsset) {
          const cost = typeof selectedAsset.acquisitionCost === 'number' && !isNaN(selectedAsset.acquisitionCost)
            ? selectedAsset.acquisitionCost
            : 0;
          return generateAssetDecayData(selectedAsset.id, cost);
        }

        console.warn('⚠️ Selected asset not found for graph, using default mock data');
        return defaultMonthlyData;
      }
    } catch (err) {
      console.error('❌ Error generating graph data:', err);
      return defaultMonthlyData;
    }
  }, [assets, selectedAssetId, defaultMonthlyData]);

  /**
   * Calculate Starting Value (Acquisition Cost) from API data
   * - For 'all': Sum of all assets' acquisitionCost
   * - For specific asset: Individual asset's acquisitionCost
   * - Handles null/undefined values gracefully
   */
  const startingValue = useMemo(() => {
    try {
      // If no assets data available, use fallback from mock data
      if (!assets || assets.length === 0) {
        console.warn('⚠️ No assets data available for starting value, using fallback');
        return monthlyData[0]?.value || 0;
      }

      if (selectedAssetId === 'all') {
        // Calculate sum of all assets' acquisition costs
        const totalAcquisitionCost = assets.reduce((sum: any, asset: any) => {
          // Handle null/undefined/invalid values
          const cost = typeof asset.acquisitionCost === 'number' && !isNaN(asset.acquisitionCost)
            ? asset.acquisitionCost
            : 0;
          return sum + cost;
        }, 0);

        return totalAcquisitionCost;
      } else {
        // Get specific asset's acquisition cost
        const selectedAsset = assets.find(a => a.id === selectedAssetId);
        if (selectedAsset) {
          const cost = typeof selectedAsset.acquisitionCost === 'number' && !isNaN(selectedAsset.acquisitionCost)
            ? selectedAsset.acquisitionCost
            : 0;
          return cost;
        }

        // Asset not found, use fallback
        console.warn('⚠️ Selected asset not found for starting value, using fallback');
        return monthlyData[0]?.value || 0;
      }
    } catch (err) {
      console.error('❌ Error calculating starting value:', err);
      return monthlyData[0]?.value || 0;
    }
  }, [assets, selectedAssetId, monthlyData]);

  // Calculate percentage change from starting value (acquisition cost)
  const percentageChange = startingValue > 0 ? ((currentValue - startingValue) / startingValue) * 100 : 0;
  const isPositive = percentageChange >= 0;

  // Calculate Y-axis ticks and scaling
  const values = monthlyData.map(d => d.value);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const yAxisTicks = useMemo(() => calculateYAxisTicks(dataMin, dataMax, 5), [dataMin, dataMax]);
  const minValue = yAxisTicks[0];
  const maxValue = yAxisTicks[yAxisTicks.length - 1];
  const valueRange = maxValue - minValue;

  // Format currency
  const formatCurrency = (value: number) => {
    return `£${value.toLocaleString('en-GB')}`;
  };

  return (
    <Card
      className={cn(
        'relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black',
        className
      )}
    >

      <CardHeader className="relative flex flex-row items-start justify-between space-y-0 pb-3">
        <div className="space-y-1.5">
          <CardTitle className="text-base font-semibold tracking-tight">
            Net Book Value Trend
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Net Book Value year to date
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80"
              >
                <Info className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" align="end" className="w-80 p-4" sideOffset={8}>
              <div className="space-y-2">
                <div className="text-sm font-semibold">What does this mean?</div>
                <div className="text-sm text-muted-foreground leading-relaxed">
                  This chart shows the Net Book Value (NBV) of your assets from the start
                  of the fiscal year (April) to the current month. NBV represents the current
                  value of assets after accounting for depreciation, reflecting their worth
                  on your balance sheet.
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="relative space-y-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Asset Filter */}
        <div className="flex items-center gap-2">
          <label htmlFor="nbv-asset-filter" className="text-sm font-medium text-muted-foreground whitespace-nowrap">
            Asset:
          </label>
          <Select value={selectedAssetId} onValueChange={setSelectedAssetId}>
            <SelectTrigger
              id="nbv-asset-filter"
              className="h-9 w-full"
            >
              <SelectValue placeholder="Select asset" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto">
              <SelectItem value="all">All</SelectItem>
              {sortedAssets.map((asset) => (
                <SelectItem key={asset.id} value={asset.id}>
                  {asset.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Net Book Value Display */}
        <div className="rounded-lg border bg-muted/30 px-4 py-3">
          <div className="text-xs font-medium text-muted-foreground mb-1">
            Current Net Book Value
          </div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(currentValue)}
          </div>
          <div className="flex items-center gap-1.5 text-xs mt-1">
            <TrendingUp className={cn(
              "h-3 w-3",
              isPositive ? "text-green-500" : "text-red-500"
            )} />
            <span className={cn(
              "font-medium",
              isPositive ? "text-green-500" : "text-red-500"
            )}>
              {Math.abs(percentageChange).toFixed(1)}%
            </span>
            <span className="text-muted-foreground">since April</span>
          </div>
        </div>

        {/* Line Graph */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Fiscal Year Progress</span>
            <span className="font-medium">
              {monthlyData[0]?.month} → {monthlyData[monthlyData.length - 1]?.month}
            </span>
          </div>
          <div className="relative h-40 w-full">
            <svg
              viewBox="0 0 600 160"
              className="h-full w-full"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Y-Axis */}
              <line
                x1="70"
                y1="20"
                x2="70"
                y2="130"
                stroke="hsl(var(--foreground))"
                strokeWidth="2"
                opacity="0.3"
              />

              {/* Y-Axis Ticks and Labels */}
              {yAxisTicks.map((tick, index) => {
                const y = normalizeToSVG(tick, minValue, maxValue, 160, 20);
                return (
                  <g key={index}>
                    {/* Tick mark */}
                    <line
                      x1="63"
                      y1={y}
                      x2="70"
                      y2={y}
                      stroke="hsl(var(--foreground))"
                      strokeWidth="2"
                      opacity="0.3"
                    />
                    {/* Grid line */}
                    <line
                      x1="70"
                      y1={y}
                      x2="580"
                      y2={y}
                      stroke="hsl(var(--border))"
                      strokeWidth="0.5"
                      strokeDasharray="2,2"
                      opacity="0.2"
                    />
                    {/* Label */}
                    <text
                      x="60"
                      y={y + 4}
                      textAnchor="end"
                      fontSize="9"
                      fill="hsl(var(--muted-foreground))"
                      fontWeight="500"
                      className="select-none"
                    >
                      {formatCurrencyUtil(tick)}
                    </text>
                  </g>
                );
              })}

              {/* Gradient fill under line */}
              <defs>
                <linearGradient id="nbvGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="hsl(142 76% 36%)" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="hsl(142 76% 36%)" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Area under the line */}
              <polygon
                points={`70,130 ${monthlyData
                  .map((dataPoint, index) => {
                    const x = 80 + index * 65;
                    const y = normalizeToSVG(dataPoint.value, minValue, maxValue, 160, 20);
                    return `${x},${y}`;
                  })
                  .join(' ')} ${80 + (monthlyData.length - 1) * 65},130`}
                fill="url(#nbvGradient)"
              />

              {/* Line path */}
              <polyline
                points={monthlyData
                  .map((dataPoint, index) => {
                    const x = 80 + index * 65;
                    const y = normalizeToSVG(dataPoint.value, minValue, maxValue, 160, 20);
                    return `${x},${y}`;
                  })
                  .join(' ')}
                fill="none"
                stroke="hsl(142 76% 36%)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data points with hover */}
              {monthlyData.map((dataPoint, index) => {
                const x = 80 + index * 65;
                const y = normalizeToSVG(dataPoint.value, minValue, maxValue, 160, 20);
                const isHovered = hoveredPoint?.month === dataPoint.month;

                return (
                  <g key={index}>
                    <circle
                      cx={x}
                      cy={y}
                      r={isHovered ? 5 : 3.5}
                      fill="hsl(142 76% 36%)"
                      stroke="hsl(var(--background))"
                      strokeWidth="2"
                      className="cursor-pointer transition-all"
                      onMouseEnter={() => setHoveredPoint(dataPoint)}
                      onMouseLeave={() => setHoveredPoint(null)}
                    />

                    {/* Month label */}
                    <text
                      x={x}
                      y="145"
                      textAnchor="middle"
                      fontSize="9"
                      fill="hsl(var(--muted-foreground))"
                      fontWeight="500"
                      className="select-none"
                    >
                      {dataPoint.month}
                    </text>

                    {/* Hover tooltip */}
                    {isHovered && (
                      <g>
                        <rect
                          x={x - 35}
                          y={y - 38}
                          width="70"
                          height="28"
                          rx="4"
                          fill="hsl(var(--popover))"
                          stroke="hsl(var(--border))"
                          strokeWidth="1"
                          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                        />
                        <text
                          x={x}
                          y={y - 25}
                          textAnchor="middle"
                          fontSize="9"
                          fontWeight="600"
                          fill="hsl(var(--muted-foreground))"
                        >
                          {dataPoint.month}
                        </text>
                        <text
                          x={x}
                          y={y - 14}
                          textAnchor="middle"
                          fontSize="10"
                          fontWeight="700"
                          fill="hsl(142 76% 36%)"
                        >
                          {formatCurrency(dataPoint.value)}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border bg-muted/50 px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">
              Starting Value
            </div>
            <div className="text-sm font-semibold text-foreground">
              {formatCurrency(startingValue)}
            </div>
          </div>
          <div className="rounded-lg border bg-muted/50 px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">
              Current Value
            </div>
            <div className="text-sm font-semibold text-green-600">
              {formatCurrency(currentValue)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
